// Generates workspace-specific context for the help agent
export function generateHelpContext({ organization, me, featureToggle, accounts, suppliers, opportunities }) {
    const features = Object.entries(featureToggle || {}).filter(([k, v]) => v).map(([k]) => k).join(', ');
    const transportModes = [
        organization.defaults?.transportationMethod,
    ].filter(Boolean).join(', ') || 'air, sea, land';

    const integrations = [];
    if (organization.integrations?.cargowise?.enabled) integrations.push('CargoWise (TMS)');
    if (organization.integrations?.okargo?.enabled) integrations.push('O-Kargo (rate search)');
    if (organization.integrations?.cargofive?.enabled) integrations.push('CargoFive (rate search)');

    // Workspace data summaries
    const supplierNames = (suppliers || []).map(s => s.name || s.organization).filter(Boolean).slice(0, 100);
    const accountNames = (accounts || []).map(a => a.name).filter(Boolean).slice(0, 100);
    const userEmails = (organization.users || []).map(u => u.email);
    const templateNames = (organization.templates || []).map(t => t.title || t.name).filter(Boolean);

    // Recent opportunity stats
    const totalOpps = (opportunities || []).filter(o => o.status !== 'deleted').length;
    const draftOpps = (opportunities || []).filter(o => o.status === 'draft' || !o.status).length;
    const sentOpps = (opportunities || []).filter(o => o.status === 'sent-to-endcustomer' || o.status === 'sending-to-endcustomer').length;
    const wonOpps = (opportunities || []).filter(o => o.status === 'completed' || o.status === 'endcustomer-accepted').length;

    return `
### Organization
- Name: ${organization.name || 'Unknown'}
- Default currency: ${organization.defaults?.currency || 'USD'}
- Available transport modes: ${transportModes}
- Workspace address: ${organization.address?.text || 'Not set'}
- Current user: ${me?.email} (${me?.isAdmin ? 'admin' : 'user'})

### Enabled Integrations
${integrations.length > 0 ? integrations.map(i => `- ${i}`).join('\n') : '- None'}

### Enabled Features
${features || 'Default features'}

### Workspace Data
- **Team members** (${userEmails.length}): ${userEmails.join(', ')}
- **Suppliers in this workspace** (${supplierNames.length}): ${supplierNames.join(', ') || 'None'}
- **Accounts/Customers** (${accountNames.length}): ${accountNames.join(', ') || 'None'}
- **Opportunities**: ${totalOpps} total (${draftOpps} drafts, ${sentOpps} sent, ${wonOpps} won)
${templateNames.length > 0 ? `- **Templates**: ${templateNames.join(', ')}` : ''}

IMPORTANT: When a user mentions a supplier, account, or person name, check the lists above. If the name doesn't exist in this workspace, tell them clearly. Suggest similar names if any match partially.

### Navigation Structure
The sidebar contains these sections:
- **Opportunities** — Create and manage freight quotes (one-off, free text, contracts)
- **Mailbox** — Email integration for receiving and processing rate requests
- **Supplier Rates** — Search and manage freight rates from suppliers (admin only)
- **Quotations Analytics** — Dashboard showing quote performance, win rates, revenue, GP (requires Financials permission)
- **Sales Performance** — Sales targets tracking with KPIs, team performance, and target detail (requires Financials permission)
- **Accounts** — Customer/company management with timeline and tradelines
- **Sales Targets** — Set revenue/shipment/TEU goals per account, route, and transport mode
- **Opportunity Templates** — Reusable quote templates (admin only)
- **Supplier & Rate Forms** — Configure supplier rate card forms (admin only)
- **Users** — Manage team members, roles, and permissions (admin/manager only)
- **Suppliers** — Manage supplier companies (admin only)
- **Terms & Conditions** — Manage T&C templates (admin only)

### Key Concepts
- **Opportunity**: A freight quote/proposal. Can be one-off, free text, email-based, or a contract.
- **Offer Builder (Step 1)**: Where you set products, routes, and pricing fields with sell rates and costs.
- **Preview & Send (Step 2)**: Preview the quote and send via email, link, or export to TMS.
- **Tradeline / Sales Target**: A recurring or one-time sales goal tracking shipments, TEUs, revenue, or profit for a specific route/account.
- **Cost Center**: Right side panel showing buying rates (costs) for an opportunity. You can add costs by: searching supplier rates, pasting from free text/email, or entering manually.
- **Display Currency**: Convert the quote's displayed prices to a different currency for the customer.
- **Exchange Rate Lock**: Exchange rates are automatically locked when a quote is sent, preserving the exact rates used.
- **Profit Bar**: Shows Sell, Cost, GP, and Margin for an opportunity. Appears on Step 1 (sticky bottom) and Step 2.
- **Pipeline Phase**: Sales targets have phases: New → Identified → Eligible → Quotation Proposal → Won.
- **Uplift / Buying Margin**: A formula that sets the sell price as a percentage markup on the buying cost.
- **Financial Visibility**: Controlled per user via the "Financials" flag in Users settings.

### Common Tasks
- **Create an opportunity**: Click "New Opportunity" on the Opportunities page, choose type (Quick Quote, Form Builder, or Contract).
- **Add products**: In Step 1, click the Products section to add containers (sea), air freight, or land freight items.
- **Set routes**: In Step 1, use the route selector to set origin and destination.
- **Add costs**: Open the Cost Center (right panel) to search for supplier rates or paste rates from free text.
- **Add costs manually**: In the Cost Center, click "Add Costs Manually", select a supplier from the dropdown, then enter the rates.
- **Paste rates from email**: In the Cost Center, click "Rates from Free Text", paste the email content, and the system will parse it.
- **Apply uplift**: In a field's formula editor, use "Buying Margin" to set a % markup on costs.
- **Set display currency**: Click the currency icon on a section header to convert displayed prices.
- **Send a quote**: Go to Step 2, click "Send by Email" or "Generate Link".
- **Duplicate an opportunity**: Use the 3-dot menu on the opportunity header.
- **Create a sales target**: Go to Sales Targets, click "New Target", set account, routes, goals, and period.
- **Filter opportunities**: Use the filter toolbar with status pills, date ranges, search, and tags.
- **Upload rate files**: Go to Supplier Rates, click "Import" to upload rate files (CSV, Excel). Select the supplier, map the columns, and import. This is the primary way to bulk-import rates.
- **Add a new supplier**: Go to Settings > Suppliers (admin only), click "Add Supplier".
- **Edit comments**: Click the edit icon on any comment in the Updates & Comments panel.
- **Lock/unlock exchange rates**: Rates auto-lock on send. Manually unlock via the 3-dot settings menu on the opportunity.

### Free Upload Template (Supplier Rates Import)
To bulk-import supplier rates, go to **Supplier Rates → Import** and use the **Free Upload Template**. Download the CSV template, fill it in, and upload. Files that pass all validation are processed free of charge.

**Required columns:**
- **source** — UN/LOCODE for origin port (e.g. CNNGB). If unknown, enter city name + country (e.g. Ningbo, China).
- **destination** — UN/LOCODE for destination port (e.g. RSBEG). Same rules as source.
- **transitTime** — Transit time in days (numeric only, no text/ranges). Enter -1 if unknown.
- **transportationMethod** — Must be \`sea\`. Case-sensitive.
- **validFrom** — Start date in \`yyyy-mm-dd\` format.
- **validUntil** — End date in \`yyyy-mm-dd\` format. Must be on or after validFrom.
- **form** — Must be \`AGENTS\`. Case-sensitive.
- **supplier** — Agent or carrier company name.
- **supplierEmail** — Contact email for the supplier.
- **product** — Container type. Case-sensitive. Options: 20' Dry, 20' Flat, 20' Open Top, 20' Reefer, 40' Dry, 40' Flat, 40' Open Top, 40' Reefer, 40' HC Dry, 40' HC Flat, 40' HC Open Top, 40' HC Reefer, 45' HC Dry, 45' HC Reefer, LCL, Air freight, Dry, Reefer, Tent, Mega.
- **dangerous** — \`TRUE\` or \`FALSE\`. Case-sensitive.
- **type** — Must be \`contract\`. Case-sensitive.

**Optional columns:**
- **transshipment** — Free text. Transshipment port if applicable.
- **Carrier** — Shipping line name (e.g. MSC, CMA CGM).
- **Charge columns** (Ocean Freight, THC, etc.) — Each charge gets its own column. Values in \`value|CURRENCY\` format (e.g. \`1850|USD\`, \`520|EUR\`). Add as many as needed.
- **Notes / Remarks** — Any free text column for notes. Columns without the \`value|CURRENCY\` format are treated as free text.

**Validation:** The system validates all required fields, date formats, product types, and charge formats before upload. Errors are shown with the filename and row details.

### Troubleshooting
- **"Do it" button not working on pasted rates**: The system may not have parsed the text correctly. Try reformatting the pasted content, or use "Add Costs Manually" instead.
- **Supplier not found**: Check the exact supplier name. The supplier must exist in Settings > Suppliers before it can be used in the Cost Center. An admin can add new suppliers.
- **Exchange rates showing wrong values**: Check if rates are locked (hover over the profit bar). For draft opportunities, rates use live values. After sending, they're locked.
`;
}
